import type { NutrientId } from "./types";

/**
 * Canonical nutrient data model — one record per nutrient (notes/COMPLIANCE-ENGINE-SPEC.md
 * §1.1 order + §6 data model). Pure data; no rounding logic lives here.
 */
export interface NutrientDef {
  id: NutrientId;
  /** Canonical internal unit (the unit the DV table is keyed in). */
  unit: "kcal" | "g" | "mg" | "mcg";
  /** Display names by locale (BCP-47). en-US is the FDA default. */
  names: Record<string, string>;
  /** Position in the §1.1 display order (1-based, strictly increasing). */
  order: number;
  /** Indentation level: 0 = major nutrient, 1 = sub, 2 = sub-sub (Added Sugars). */
  indent: 0 | 1 | 2;
  /** Bold weight per (d)(2)(iv) (major nutrients only; matches the FDA sample). */
  bold: boolean;
  /** Whether this nutrient is mandatory on a US Nutrition Facts panel. */
  mandatory: boolean;
  /**
   * Whether a %DV is shown for this row in the assembled panel (brief annotation #5).
   * False for Calories / Trans Fat / Total Sugars (no DV) and Protein (voluntary; the FDA
   * sample shows none → suppressed by default in M1, even though a protein DRV exists).
   */
  showPercentDV: boolean;
  /** Optional row text template, e.g. Added Sugars: "Includes {v}g Added Sugars". */
  rowTemplate?: string;
}

/**
 * US Nutrition Facts nutrients in the fixed (c) display order (21 CFR 101.9(c)).
 * "Calories from Fat" is intentionally absent (removed by the 2016 rule).
 */
export const US_NF_NUTRIENTS: NutrientDef[] = [
  {
    id: "energy",
    unit: "kcal",
    names: { "en-US": "Calories" },
    order: 1,
    indent: 0,
    bold: true,
    mandatory: true,
    showPercentDV: false, // no DV for calories
  },
  {
    id: "fat_total",
    unit: "g",
    names: { "en-US": "Total Fat" },
    order: 2,
    indent: 0,
    bold: true,
    mandatory: true,
    showPercentDV: true,
  },
  {
    id: "fat_sat",
    unit: "g",
    names: { "en-US": "Saturated Fat" },
    order: 3,
    indent: 1,
    bold: false,
    mandatory: true,
    showPercentDV: true,
  },
  {
    id: "fat_trans",
    unit: "g",
    names: { "en-US": "Trans Fat" }, // rendered italic ("Trans") by the renderer
    order: 4,
    indent: 1,
    bold: false,
    mandatory: true,
    showPercentDV: false, // no DV established for trans fat
  },
  {
    id: "cholesterol",
    unit: "mg",
    names: { "en-US": "Cholesterol" },
    order: 5,
    indent: 0,
    bold: true,
    mandatory: true,
    showPercentDV: true,
  },
  {
    id: "sodium",
    unit: "mg",
    names: { "en-US": "Sodium" },
    order: 6,
    indent: 0,
    bold: true,
    mandatory: true,
    showPercentDV: true,
  },
  {
    id: "carb_total",
    unit: "g",
    names: { "en-US": "Total Carbohydrate" },
    order: 7,
    indent: 0,
    bold: true,
    mandatory: true,
    showPercentDV: true,
  },
  {
    id: "fiber",
    unit: "g",
    names: { "en-US": "Dietary Fiber" },
    order: 8,
    indent: 1,
    bold: false,
    mandatory: true,
    showPercentDV: true,
  },
  {
    id: "sugars_total",
    unit: "g",
    names: { "en-US": "Total Sugars" },
    order: 9,
    indent: 1,
    bold: false,
    mandatory: true,
    showPercentDV: false, // no DV for total sugars
  },
  {
    id: "sugars_added",
    unit: "g",
    names: { "en-US": "Added Sugars" },
    order: 10,
    indent: 2,
    bold: false,
    mandatory: true,
    showPercentDV: true,
    rowTemplate: "Includes {v}g Added Sugars",
  },
  {
    id: "protein",
    unit: "g",
    names: { "en-US": "Protein" },
    order: 11,
    indent: 0,
    bold: true,
    mandatory: true,
    showPercentDV: false, // %DV voluntary per (c)(7); FDA sample shows none → default off
  },
  {
    id: "vitamin_d",
    unit: "mcg",
    names: { "en-US": "Vitamin D" },
    order: 12,
    indent: 0,
    bold: false,
    mandatory: true,
    showPercentDV: true,
  },
  {
    id: "calcium",
    unit: "mg",
    names: { "en-US": "Calcium" },
    order: 13,
    indent: 0,
    bold: false,
    mandatory: true,
    showPercentDV: true,
  },
  {
    id: "iron",
    unit: "mg",
    names: { "en-US": "Iron" },
    order: 14,
    indent: 0,
    bold: false,
    mandatory: true,
    showPercentDV: true,
  },
  {
    id: "potassium",
    unit: "mg",
    names: { "en-US": "Potassium" },
    order: 15,
    indent: 0,
    bold: false,
    mandatory: true,
    showPercentDV: true,
  },
];

const BY_ID: ReadonlyMap<NutrientId, NutrientDef> = new Map(
  US_NF_NUTRIENTS.map((n) => [n.id, n]),
);

/** Look up a nutrient definition by id (undefined if not in the US NF set). */
export function getNutrient(id: NutrientId): NutrientDef | undefined {
  return BY_ID.get(id);
}

/**
 * Display a micro quantitative amount in FDA-sample style: e.g. "2 mcg", "260 mg", "8 mg".
 *
 * NEEDS-VERIFICATION (spec §1.2 / §10 #2): the reg ties micro precision to "levels of
 * significance" + "additional significance for small amounts" rather than a fixed grid.
 * This reproduces the published FDA-sample style (whole units; one decimal only for small
 * values; trailing decimal zeros dropped) and does NOT invent a per-mcg/per-mg increment.
 */
export function displayMicroAmount(id: NutrientId, mcgOrMg: number): string {
  const def = getNutrient(id);
  const unit = def?.unit ?? "mg";
  // Whole units at/above 10; one decimal place for smaller amounts (then trim trailing .0).
  const rounded =
    Math.abs(mcgOrMg) >= 10 ? Math.round(mcgOrMg) : Math.round(mcgOrMg * 10) / 10;
  const num = Number(rounded.toFixed(1)).toString(); // drops a trailing ".0"
  return `${num} ${unit}`;
}
